import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  ComplianceProgramState,
  GetComplianceProgramAvailableAppliesToUpdatesQuery,
  GetComplianceProgramAvailableAppliesToUpdatesQueryVariables,
  GetComplianceProgramQuery,
  GetComplianceProgramQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import EngagementSurveyReport from "@apps/reporting/components/engagement-survey-report";
import { currentUserVar, errorNotificationVar, isAdminVar } from "@cache/cache";
import AppLabel from "@components/draft-label/app-label";
import DraftLabel from "@components/draft-label/draft-label";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import Tabs from "@components/tabs/tabs";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { assertEdgesNonNull } from "@helpers/helpers";
import usePkParams from "@helpers/hooks/use-pk-params";

import getComplianceProgramAvailableAppliesToUpdatesQuery from "../graphql/get-compliance-program-available-applies-to-updates-query";
import getComplianceProgramQuery from "../graphql/get-compliance-program-query";
import { useProgramsSection } from "../programs-section-context";
import ComplianceProgramActionDropdown from "./compliance-program-action-dropdown";
import ComplianceProgramActions from "./compliance-program-actions";
import ComplianceProgramActivity from "./compliance-program-activity";
import ComplianceProgramProgress from "./compliance-program-progress";
import ComplianceProgramReminders from "./compliance-program-reminders";
import ComplianceProgramTimeline from "./compliance-program-timeline";
import AssessmentReportTab from "./program-reporting/assessment-report-tab";

enum ComplianceProgramDetailTabType {
  Actions = "My Actions",
  Progress = "Progress",
  Reporting = "Reporting",
  Reminders = "Reminders",
  Timeline = "Timeline",
  Activity = "Activity",
}

enum ComplianceProgramReportingTab {
  Manager = "manager",
  Upward = "upward",
  Engagement = "engagement",
}

type ComplianceProgramDetailTab = {
  type: ComplianceProgramDetailTabType;
  reportTab?: ComplianceProgramReportingTab;
};

const ComplianceProgram = () => {
  const { basePath, rootCrumb, noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const currentUser = currentUserVar();
  const directReportIds = assertEdgesNonNull(currentUser.directReports).map(
    ({ id }) => id,
  );
  const reportingTreeIds = compact(currentUser.reportingTreeIds).filter(
    (id) => !directReportIds.includes(id),
  );
  const isAdmin = isAdminVar();
  const isManager = directReportIds.length > 0;
  const hasReportingTree = reportingTreeIds.length > 0;
  const location = useLocation<TFLocationState>();
  const complianceProgramId = usePkParams("complianceProgramId");
  const { reportTab } = useParams<{
    reportTab?: ComplianceProgramReportingTab;
  }>();
  const url = `${basePath}/${complianceProgramId}/progress`;
  const [selectedTab, setSelectedTab] = useState<ComplianceProgramDetailTab>(
    location.pathname.includes("/actions")
      ? { type: ComplianceProgramDetailTabType.Actions }
      : location.pathname.includes("/activity")
        ? { type: ComplianceProgramDetailTabType.Activity }
        : location.pathname.includes("/timeline")
          ? { type: ComplianceProgramDetailTabType.Timeline }
          : location.pathname.includes("/reporting")
            ? { type: ComplianceProgramDetailTabType.Reporting, reportTab }
            : isAdmin || isManager || hasReportingTree
              ? { type: ComplianceProgramDetailTabType.Progress }
              : {
                  type: ComplianceProgramDetailTabType.Reporting,
                  reportTab: ComplianceProgramReportingTab.Manager,
                },
  );
  const link = useLink();

  const { data: complianceProgramData, loading: isLoadingComplianceProgram } =
    useQuery<GetComplianceProgramQuery, GetComplianceProgramQueryVariables>(
      getComplianceProgramQuery,
      {
        fetchPolicy: "network-only",
        variables: { complianceProgramId },
      },
    );

  const { data: availableAppliesToUpdates } = useQuery<
    GetComplianceProgramAvailableAppliesToUpdatesQuery,
    GetComplianceProgramAvailableAppliesToUpdatesQueryVariables
  >(getComplianceProgramAvailableAppliesToUpdatesQuery, {
    fetchPolicy: "network-only",
    variables: { complianceProgramId },
  });

  const complianceProgram = useMemo(
    () => complianceProgramData?.complianceProgram,
    [complianceProgramData],
  );

  useDocumentTitle(complianceProgram?.title || `${noun} Reporting`);

  const handleChangeTab = (option: ComplianceProgramDetailTab) => {
    if (option.type === ComplianceProgramDetailTabType.Actions) {
      link.redirect(`${basePath}/${complianceProgramId}/actions`);
    } else if (option.type === ComplianceProgramDetailTabType.Reporting) {
      link.redirect(
        `${basePath}/${complianceProgramId}/reporting/${option.reportTab}`,
      );
    } else if (option.type === ComplianceProgramDetailTabType.Reminders) {
      link.redirect(`${basePath}/${complianceProgramId}/reminders`);
    } else if (option.type === ComplianceProgramDetailTabType.Activity) {
      link.redirect(`${basePath}/${complianceProgramId}/activity`);
    } else if (option.type === ComplianceProgramDetailTabType.Timeline) {
      link.redirect(`${basePath}/${complianceProgramId}/timeline`);
    } else {
      link.redirect(`${basePath}/${complianceProgramId}/progress`);
    }
  };

  const programTabOptions = useMemo(() => {
    if (!complianceProgram) {
      return [];
    }

    const progress: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Progress,
    };
    const reminders: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Reminders,
    };
    const actions: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Actions,
    };
    const activity: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Activity,
    };
    const timeline: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Timeline,
    };
    // Combined review results tab (downward + upward switch lives inside it).
    const reviewResults: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Reporting,
      reportTab: ComplianceProgramReportingTab.Manager,
    };
    const engagementReporting: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Reporting,
      reportTab: ComplianceProgramReportingTab.Engagement,
    };
    return compact([
      (isAdmin || isManager || hasReportingTree) && {
        value: progress,
        label: ComplianceProgramDetailTabType.Progress,
      },
      (complianceProgram.performanceAssessmentTemplate ||
        complianceProgram.managerAssessmentTemplate) && {
        value: reviewResults,
        label: "Results",
      },
      complianceProgram.engagementSurveyTemplate && {
        value: engagementReporting,
        label: "Results",
      },
      complianceProgram.state === ComplianceProgramState.Published && {
        value: actions,
        label: ComplianceProgramDetailTabType.Actions,
      },
      complianceProgram.canUpdate?.permission && {
        value: reminders,
        label: ComplianceProgramDetailTabType.Reminders,
      },
      // Same gate as Reminders: canUpdate is admin settings access or being the
      // creator, which is the branch of the event authorization filter that returns
      // every row. A manager would now see something here - the sends name the people
      // they reached - but showing them a tab of only their own reports' rows is a
      // separate product decision, so the gate stays where it is.
      complianceProgram.canUpdate?.permission && {
        value: activity,
        label: ComplianceProgramDetailTabType.Activity,
      },
      // Same gate again: the Timeline names the whole roster and who is left out
      // of every send, which only a cycle's admin may see.
      complianceProgram.canUpdate?.permission && {
        value: timeline,
        label: ComplianceProgramDetailTabType.Timeline,
      },
    ]);
  }, [complianceProgram, hasReportingTree, isAdmin, isManager]);

  // The tab strip is the only list of tabs a person may open, so a URL naming one
  // that is not on it -- /timeline, /activity or /reminders for somebody who cannot
  // administer the cycle -- falls back to the first tab they do have. Without this
  // they land on an empty panel saying nothing is scheduled, which is not why it is
  // empty.
  const visibleTab = useMemo(() => {
    if (programTabOptions.length === 0) {
      return selectedTab;
    }
    const allowed = programTabOptions.some(
      (option) => option.value.type === selectedTab.type,
    );
    return allowed ? selectedTab : programTabOptions[0].value;
  }, [programTabOptions, selectedTab]);

  useEffect(() => {
    if (location.pathname.includes("/progress")) {
      setSelectedTab({ type: ComplianceProgramDetailTabType.Progress });
    } else if (location.pathname.includes("/actions")) {
      setSelectedTab({ type: ComplianceProgramDetailTabType.Actions });
    } else if (location.pathname.includes("/reminders")) {
      setSelectedTab({ type: ComplianceProgramDetailTabType.Reminders });
    } else if (location.pathname.includes("/activity")) {
      setSelectedTab({ type: ComplianceProgramDetailTabType.Activity });
    } else if (location.pathname.includes("/timeline")) {
      setSelectedTab({ type: ComplianceProgramDetailTabType.Timeline });
    } else if (location.pathname.includes("/reporting")) {
      setSelectedTab({
        type: ComplianceProgramDetailTabType.Reporting,
        reportTab,
      });
    }
  }, [location.pathname, reportTab]);

  useEffect(() => {
    if (
      complianceProgram?.canReadDetails &&
      !complianceProgram?.canReadDetails.permission
    ) {
      errorNotificationVar({
        title: `You do not have permission to view this ${nounLower}.`,
        timeout: 10000,
      });
      link.redirect(rootCrumb.url);
    }
  }, [complianceProgram?.canReadDetails, link, rootCrumb.url, nounLower]);

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={compact([
          { title: rootCrumb.title, url: rootCrumb.url },
          complianceProgram && {
            title: (
              <div className="flex items-center gap-3">
                <div>{complianceProgram.title}</div>
                <div>
                  {complianceProgram.state === ComplianceProgramState.Draft && (
                    <DraftLabel />
                  )}
                  {complianceProgram.state ===
                    ComplianceProgramState.Paused && (
                    <AppLabel
                      label="Paused"
                      className="text-amber-900 bg-amber-200"
                    />
                  )}
                </div>
              </div>
            ),
            url: url,
          },
        ])}
      >
        {complianceProgram && (
          <div className="flex items-center gap-4">
            <ComplianceProgramActionDropdown
              complianceProgram={complianceProgram}
              availableAppliesToUpdates={
                availableAppliesToUpdates?.complianceProgram
                  ?.availableAppliesToUpdates
              }
            />
          </div>
        )}
      </Layout.Header>
      <Layout.Container>
        <main className="absolute inset-0 overflow-auto">
          <div className="@container/layout-main mx-auto">
            {programTabOptions.length > 0 && (
              <div className="z-30 px-8 pt-6 pb-4 sticky top-0 bg-white">
                <Tabs
                  options={programTabOptions}
                  selectedValue={visibleTab}
                  onClickOptionValue={handleChangeTab}
                />
              </div>
            )}
            <div className="w-full">
              {isLoadingComplianceProgram ? (
                <div className="w-full">
                  <Loading className="mt-12">Loading {nounLower}...</Loading>
                </div>
              ) : complianceProgram &&
                complianceProgram.canReadDetails.permission ? (
                <>
                  {visibleTab.type ===
                    ComplianceProgramDetailTabType.Actions && (
                    <div className="px-8 pb-12">
                      <ComplianceProgramActions
                        complianceProgramId={complianceProgram.id}
                      />
                    </div>
                  )}
                  {visibleTab.type ===
                    ComplianceProgramDetailTabType.Progress && (
                    <div className="px-8 pb-12">
                      <ComplianceProgramProgress
                        complianceProgramId={complianceProgram.id}
                      />
                    </div>
                  )}
                  {visibleTab.type ===
                    ComplianceProgramDetailTabType.Reporting &&
                    visibleTab.reportTab ===
                      ComplianceProgramReportingTab.Manager && (
                      <AssessmentReportTab
                        complianceProgramId={complianceProgram.id}
                        hasDownward={
                          !!complianceProgram.performanceAssessmentTemplate
                        }
                        hasUpward={
                          !!complianceProgram.managerAssessmentTemplate
                        }
                      />
                    )}
                  {visibleTab.type ===
                    ComplianceProgramDetailTabType.Reporting &&
                    visibleTab.reportTab ===
                      ComplianceProgramReportingTab.Engagement && (
                      <EngagementSurveyReport
                        complianceProgramId={complianceProgram.id}
                      />
                    )}
                  {visibleTab.type ===
                    ComplianceProgramDetailTabType.Reminders && (
                    <div className="px-8 pb-12">
                      <ComplianceProgramReminders
                        complianceProgram={complianceProgram}
                        complianceProgramId={complianceProgram.id}
                      />
                    </div>
                  )}
                  {visibleTab.type ===
                    ComplianceProgramDetailTabType.Activity && (
                    <div className="px-8 pb-12">
                      <ComplianceProgramActivity
                        complianceProgramId={complianceProgram.id}
                      />
                    </div>
                  )}
                  {visibleTab.type ===
                    ComplianceProgramDetailTabType.Timeline && (
                    <div className="px-8 pb-12">
                      <ComplianceProgramTimeline
                        complianceProgramId={complianceProgram.id}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full flex justify-center mt-10">
                  <div className="text-center text-gray-500 text-sm">
                    Program not found.
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </Layout.Container>
    </Layout>
  );
};

export default ComplianceProgram;
